import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  PlaylistAdd as PlaylistAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { ProcessSection } from "../../../api/process-sections";
import {
  sectionTitleStyles,
  accordionStyles,
  accordionSummaryStyles,
  accordionContentStyles,
  sectionContentStyles,
} from "../styles";
import { useSectionDeselectConfirmation } from "../hooks";
import { StageEditForm } from "./StageEditForm";
import { StageList } from "./StageList";
import { EditingStage, LocalStage, LocalReasonSolution } from "../types";

interface ProcessSectionSelectorProps {
  processSections: ProcessSection[];
  selectedSections: number[];
  onSectionToggle: (sectionId: number) => void;
  onAddStageClick: (sectionId: number) => void;
  onSectionDeselect: (sectionId: number) => void;
  children?: React.ReactNode;
  isEditing?: boolean;
  onShowConfirmation: (sectionId: number, sectionName: string) => void;
  sectionVisibility?: { [sectionId: number]: boolean };
  onToggleSectionVisibility?: (sectionId: number) => void;
  // Stage Edit Form props
  editingStage: EditingStage;
  stageError: string;
  onSaveStage: () => void;
  onCancelEditingStage: () => void;
  onStageNumberChange: (value: number) => void;
  onTitleChange: (value: string) => void;
  // Stage List props
  stages: LocalStage[];
  reasonSolutions: LocalReasonSolution[];
  onAddReasonSolutionClick: (stageId: string) => void;
  onEditStage: (stage: LocalStage) => void;
  onDeleteStage: (stageId: string) => void;
  onEditReasonSolution: (rs: LocalReasonSolution) => void;
  onDeleteReasonSolution: (reasonSolutionId: string) => void;
  getReasonSolutionsForStage: (stageId: string) => LocalReasonSolution[];
  // Reason Solution Form props
  editingReasonSolution: any;
  reasonSolutionError: string;
  onSaveReasonSolution: () => void;
  onCancelEditingReasonSolution: () => void;
  onRowChange: (value: number) => void;
  onIsPrimaryChange: (value: boolean) => void;
  onReasonChange: (value: string) => void;
  onSolutionChange: (value: string) => void;
  onPreviousReasonIdsChange: (value: string) => void;
  // Stage Edit Form props
  onIsPublicChange: (value: boolean) => void;
  onFollowUpGroupIdsChange: (value: string) => void;
  followUpGroups: any[];
}

export const ProcessSectionSelector: React.FC<ProcessSectionSelectorProps> = ({
  processSections,
  selectedSections,
  onSectionToggle,
  onAddStageClick,
  onSectionDeselect,
  children,
  isEditing = false,
  onShowConfirmation,
  sectionVisibility = {},
  onToggleSectionVisibility,
  // Stage Edit Form props
  editingStage,
  stageError,
  onSaveStage,
  onCancelEditingStage,
  onStageNumberChange,
  onTitleChange,
  // Stage List props
  stages,
  reasonSolutions,
  onAddReasonSolutionClick,
  onEditStage,
  onDeleteStage,
  onEditReasonSolution,
  onDeleteReasonSolution,
  getReasonSolutionsForStage,
  // Reason Solution Form props
  editingReasonSolution,
  reasonSolutionError,
  onSaveReasonSolution,
  onCancelEditingReasonSolution,
  onRowChange,
  onIsPrimaryChange,
  onReasonChange,
  onSolutionChange,
  onPreviousReasonIdsChange,
  // Stage Edit Form props
  onIsPublicChange,
  onFollowUpGroupIdsChange,
  followUpGroups,
}) => {
  const handleSectionToggle = (sectionId: number) => {
    const isCurrentlySelected = selectedSections.includes(sectionId);

    if (isCurrentlySelected && isEditing) {
      // در حالت ویرایش، اگر بخش انتخاب شده است، دیالوگ تایید نمایش داده شود
      const section = processSections.find((s) => s.id === sectionId);
      onShowConfirmation(sectionId, section?.name || `بخش ${sectionId}`);
    } else {
      // در حالت عادی یا افزودن بخش جدید
      onSectionToggle(sectionId);
    }
  };

  const handleToggleVisibility = (sectionId: number) => {
    if (onToggleSectionVisibility) {
      onToggleSectionVisibility(sectionId);
    }
  };

  const isSectionVisible = (sectionId: number) => {
    return sectionVisibility[sectionId] !== false; // Default to true if not specified
  };
  return (
    <Box>
      {processSections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          هیچ بخش فرایندی تعریف نشده است. ابتدا بخش‌های فرایند را در منوی
          تنظیمات ایجاد کنید.
        </Typography>
      ) : (
        processSections.map((section) => {
          const isVisible = isSectionVisible(section.id);

          return (
            <Accordion
              key={section.id}
              sx={{
                ...accordionStyles,
                opacity: isVisible ? 1 : 0.5,
                border: isVisible ? undefined : "2px dashed #ccc",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${section.id}-content`}
                id={`panel${section.id}-header`}
                sx={accordionSummaryStyles}
              >
                <Box sx={accordionContentStyles}>
                  <Tooltip
                    title={
                      selectedSections.includes(section.id)
                        ? "حذف بخش از فرایند"
                        : "افزودن بخش به فرایند"
                    }
                    arrow
                  >
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSectionToggle(section.id);
                      }}
                      size="small"
                    >
                      {selectedSections.includes(section.id) ? (
                        <CheckBoxIcon color="primary" />
                      ) : (
                        <CheckBoxOutlineBlankIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      flexGrow: 1,
                      textDecoration: isVisible ? "none" : "line-through",
                      color: isVisible ? "inherit" : "text.secondary",
                    }}
                  >
                    بخش {section.sectionNumber}: {section.name}
                    {!isVisible && " (مخفی)"}
                  </Typography>

                  {/* Visibility Toggle Button */}
                  {onToggleSectionVisibility && (
                    <Tooltip
                      title={isVisible ? "مخفی کردن بخش" : "نمایش بخش"}
                      arrow
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVisibility(section.id);
                        }}
                        size="small"
                        color={isVisible ? "primary" : "default"}
                      >
                        {isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip
                    title={
                      !selectedSections.includes(section.id)
                        ? "ابتدا بخش را انتخاب کنید"
                        : !isVisible
                          ? "ابتدا بخش را نمایش دهید"
                          : "افزودن مرحله"
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddStageClick(section.id);
                        }}
                        size="small"
                        color="primary"
                        disabled={
                          !selectedSections.includes(section.id) || !isVisible
                        }
                      >
                        <PlaylistAddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={sectionContentStyles}>
                  {isVisible ? (
                    <>
                      {/* Stage Edit Form - Only show for this specific section */}
                      {editingStage.sectionId === section.id && (
                        <StageEditForm
                          editingStage={editingStage}
                          stageError={stageError}
                          onSave={onSaveStage}
                          onCancel={onCancelEditingStage}
                          onStageNumberChange={onStageNumberChange}
                          onTitleChange={onTitleChange}
                          onIsPublicChange={onIsPublicChange}
                          onFollowUpGroupIdsChange={onFollowUpGroupIdsChange}
                          followUpGroups={followUpGroups}
                          isNewStage={!editingStage.stageId}
                        />
                      )}

                      {/* Stage List - Only show for this specific section */}
                      <StageList
                        stages={stages.filter(
                          (stage) => stage.sectionId === section.id
                        )}
                        reasonSolutions={reasonSolutions}
                        editingStage={editingStage}
                        stageError={stageError}
                        onAddReasonSolutionClick={onAddReasonSolutionClick}
                        onEditStage={onEditStage}
                        onDeleteStage={onDeleteStage}
                        onEditReasonSolution={onEditReasonSolution}
                        onDeleteReasonSolution={onDeleteReasonSolution}
                        getReasonSolutionsForStage={getReasonSolutionsForStage}
                        // Reason Solution Form props
                        editingReasonSolution={editingReasonSolution}
                        reasonSolutionError={reasonSolutionError}
                        onSaveReasonSolution={onSaveReasonSolution}
                        onCancelEditingReasonSolution={
                          onCancelEditingReasonSolution
                        }
                        onRowChange={onRowChange}
                        onIsPrimaryChange={onIsPrimaryChange}
                        onReasonChange={onReasonChange}
                        onSolutionChange={onSolutionChange}
                        onPreviousReasonIdsChange={onPreviousReasonIdsChange}
                        // Stage Edit Form props
                        onSaveStage={onSaveStage}
                        onCancelEditingStage={onCancelEditingStage}
                        onStageNumberChange={onStageNumberChange}
                        onTitleChange={onTitleChange}
                        onIsPublicChange={onIsPublicChange}
                        onFollowUpGroupIdsChange={onFollowUpGroupIdsChange}
                        followUpGroups={followUpGroups}
                      />

                      {/* Other children */}
                      {children}
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      این بخش مخفی شده است. برای مشاهده محتوا، ابتدا بخش را
                      نمایش دهید.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};
